// client/src/setupAuthInterceptors.js
//
// Attaches the stored auth token ("Authorization: Bearer ...") to every API
// request the app makes, WITHOUT having to edit each individual call site.
//
//  - axios: all components import the same default axios instance, so a single
//    request interceptor covers every axios call.
//  - fetch: we wrap window.fetch once so the header is added to fetch() calls too.
//
// Imported for its side effects once, from index.js, before the app renders.

import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

function getToken() {
  return sessionStorage.getItem('authToken');
}

// Only attach the token to requests aimed at our own API, so we never leak it
// to third-party hosts. Treat relative URLs and our configured API base as ours.
function isOurApi(url) {
  if (!url) return false;
  if (API_BASE && url.startsWith(API_BASE)) return true;
  // Relative URL (same origin)
  return url.startsWith('/');
}

// --- axios request: attach token ---
axios.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// --- axios response: redirect to login on expired token ---
// If a 401 comes back while a token is stored, the token has expired.
// Clear auth state and reload so AuthContext reinitializes with isLoggedIn=false.
// Skip this for calls to the authenticate endpoint itself (login attempts).
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const is401 = error.response?.status === 401;
    const hasToken = Boolean(sessionStorage.getItem('authToken'));
    const isLoginCall = error.config?.url?.includes('/api/authenticate/');

    if (is401 && hasToken && !isLoginCall) {
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('isLoggedIn');
      sessionStorage.removeItem('isInstructor');
      sessionStorage.removeItem('isTA');
      sessionStorage.removeItem('username');
      sessionStorage.removeItem('fullName');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// --- fetch ---
const originalFetch = window.fetch.bind(window);
window.fetch = (input, init = {}) => {
  const url = typeof input === 'string' ? input : (input && input.url);
  const token = getToken();

  if (token && isOurApi(url)) {
    const headers = new Headers(
      (init && init.headers) || (typeof input !== 'string' && input.headers) || {}
    );
    if (!headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    init = { ...init, headers };
  }

  return originalFetch(input, init);
};
